/**
 * Console settings.
 *
 * Values an administrator may change without a deploy: the invite flow's three
 * and the three recommendation round budgets. The form reads the server's parsed
 * values rather than raw strings, so an unparsable row shows up as the default
 * that is actually in effect.
 */

import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

import {
  ACTIVATION_CODE_TTL_HOURS_MAX,
  ACTIVATION_CODE_TTL_HOURS_MIN,
  DAILY_REGISTRATION_LIMIT_MAX,
  RECOMMENDATION,
} from '@shared/constants'

import { ErrorAlert, ErrorBlock, LoadingBlock } from '../../components/layout/states'
import { Alert, AlertDescription } from '../../components/ui/alert'
import { Button } from '../../components/ui/button'
import { Card, CardContent } from '../../components/ui/card'
import { Field, FieldGroup } from '../../components/ui/field'
import { Input } from '../../components/ui/input'
import { useAdminSettings, useUpdateSettings } from '../../hooks/use-admin'
import { useCurrentUser } from '../../hooks/use-auth'
import { joinList } from '../../lib/format'
import { AdminPage, adminErrorText } from './shared'

/**
 * True when a settings box holds an integer inside the range the field allows.
 *
 * An empty box is not a value: the server rejects a missing field, so the form
 * says so rather than substituting a minimum the operator never typed.
 */
function isUsableSetting(raw: string, min: number, max: number): boolean {
  if (raw.trim().length === 0) {
    return false
  }
  const value = Number(raw)
  return Number.isInteger(value) && value >= min && value <= max
}

export function AdminSettingsPage() {
  const { t } = useTranslation()
  const { capabilities } = useCurrentUser()
  const canWrite = capabilities.includes('settings:write')

  const settings = useAdminSettings()
  const update = useUpdateSettings()

  const [applyEnabled, setApplyEnabled] = useState(true)
  const [cooldownDays, setCooldownDays] = useState('0')
  const [ttlHours, setTtlHours] = useState('72')
  const [dailyRegistrationLimit, setDailyRegistrationLimit] = useState('1000')
  const [budgetPerEvent, setBudgetPerEvent] = useState('100')
  const [budgetPerMember, setBudgetPerMember] = useState('30')
  const [budgetPerAccountDaily, setBudgetPerAccountDaily] = useState('30')
  const [invalidFields, setInvalidFields] = useState<string[]>([])

  // Seed the form from the server once it answers; afterwards the fields are
  // the operator's, so a refetch must not overwrite what they are typing.
  useEffect(() => {
    if (!settings.data) {
      return
    }
    setApplyEnabled(settings.data.inviteApplyEnabled)
    setCooldownDays(String(settings.data.inviteRejectCooldownDays))
    setTtlHours(String(settings.data.activationCodeTtlHours))
    setDailyRegistrationLimit(String(settings.data.dailyRegistrationLimit))
    setBudgetPerEvent(String(settings.data.recommendRoundBudgetPerEvent))
    setBudgetPerMember(String(settings.data.recommendRoundBudgetPerMember))
    setBudgetPerAccountDaily(String(settings.data.recommendRoundBudgetPerAccountDaily))
  }, [settings.data])

  // Read as text rather than as an `ApiError`: a request that never reached the
  // server still has to say something instead of leaving the form silent.
  const errorText = adminErrorText(update.error, t)

  /**
   * Submits the parsed values, or names the fields that are not usable.
   *
   * Every numeric field is checked before the request: an empty box used to be
   * coerced to a minimum (`|| 0`, `|| 1`), which silently stored a value the
   * operator never typed.
   */
  function submit() {
    const fields = [
      { label: t('admin.settings.cooldownDays'), raw: cooldownDays, min: 0, max: 365 },
      {
        label: t('admin.settings.ttlHours'),
        raw: ttlHours,
        min: ACTIVATION_CODE_TTL_HOURS_MIN,
        max: ACTIVATION_CODE_TTL_HOURS_MAX,
      },
      {
        label: t('admin.settings.dailyRegistrationLimit'),
        raw: dailyRegistrationLimit,
        min: 0,
        max: DAILY_REGISTRATION_LIMIT_MAX,
      },
      {
        label: t('admin.settings.roundBudgetPerEvent'),
        raw: budgetPerEvent,
        min: 1,
        max: RECOMMENDATION.roundBudgetMax,
      },
      {
        label: t('admin.settings.roundBudgetPerMember'),
        raw: budgetPerMember,
        min: 1,
        max: RECOMMENDATION.roundBudgetMax,
      },
      {
        label: t('admin.settings.roundBudgetPerAccountDaily'),
        raw: budgetPerAccountDaily,
        min: 1,
        max: RECOMMENDATION.roundBudgetMax,
      },
    ]
    const invalid = fields.filter((field) => !isUsableSetting(field.raw, field.min, field.max))
    setInvalidFields(invalid.map((field) => field.label))
    if (invalid.length > 0) {
      return
    }
    update.mutate({
      inviteApplyEnabled: applyEnabled,
      inviteRejectCooldownDays: Number(cooldownDays),
      activationCodeTtlHours: Number(ttlHours),
      dailyRegistrationLimit: Number(dailyRegistrationLimit),
      recommendRoundBudgetPerEvent: Number(budgetPerEvent),
      recommendRoundBudgetPerMember: Number(budgetPerMember),
      recommendRoundBudgetPerAccountDaily: Number(budgetPerAccountDaily),
    })
  }

  return (
    <AdminPage
      title={t('admin.settings.title')}
      description={t('admin.settings.description')}
      current={t('admin.nav.settings')}
    >
      {settings.isPending ? <LoadingBlock label={t('common.loading')} /> : null}
      {settings.isError ? (
        <ErrorBlock
          title={t('admin.common.error')}
          retryLabel={t('admin.common.retry')}
          onRetry={() => void settings.refetch()}
        />
      ) : null}

      {settings.data ? (
        <Card>
          <CardContent className="pt-4 sm:pt-5">
            <form
              className="flex flex-col gap-4"
              onSubmit={(event) => {
                event.preventDefault()
                submit()
              }}
            >
              <FieldGroup>
                <Field label={t('admin.settings.applyEnabled')} htmlFor="setting-apply">
                  <label className="flex items-center gap-2 text-sm">
                    <input
                      id="setting-apply"
                      type="checkbox"
                      checked={applyEnabled}
                      disabled={!canWrite}
                      onChange={(event) => setApplyEnabled(event.target.checked)}
                    />
                    {t('admin.settings.applyEnabledHint')}
                  </label>
                </Field>
                <Field
                  label={t('admin.settings.cooldownDays')}
                  htmlFor="setting-cooldown"
                  hint={t('admin.settings.cooldownDaysHint')}
                >
                  <Input
                    id="setting-cooldown"
                    type="number"
                    inputMode="numeric"
                    min={0}
                    max={365}
                    value={cooldownDays}
                    disabled={!canWrite}
                    onChange={(event) => setCooldownDays(event.target.value)}
                  />
                </Field>
                <Field
                  label={t('admin.settings.ttlHours')}
                  htmlFor="setting-ttl"
                  hint={t('admin.settings.ttlHoursHint', {
                    min: ACTIVATION_CODE_TTL_HOURS_MIN,
                    max: ACTIVATION_CODE_TTL_HOURS_MAX,
                  })}
                >
                  <Input
                    id="setting-ttl"
                    type="number"
                    inputMode="numeric"
                    min={ACTIVATION_CODE_TTL_HOURS_MIN}
                    max={ACTIVATION_CODE_TTL_HOURS_MAX}
                    value={ttlHours}
                    disabled={!canWrite}
                    onChange={(event) => setTtlHours(event.target.value)}
                  />
                </Field>
                <Field
                  label={t('admin.settings.dailyRegistrationLimit')}
                  htmlFor="setting-registration-limit"
                  hint={t('admin.settings.dailyRegistrationLimitHint')}
                >
                  <Input
                    id="setting-registration-limit"
                    type="number"
                    inputMode="numeric"
                    min={0}
                    max={DAILY_REGISTRATION_LIMIT_MAX}
                    value={dailyRegistrationLimit}
                    disabled={!canWrite}
                    onChange={(event) => setDailyRegistrationLimit(event.target.value)}
                  />
                </Field>
                <Field
                  label={t('admin.settings.roundBudgetPerEvent')}
                  htmlFor="setting-budget-event"
                  hint={t('admin.settings.roundBudgetHint', { max: RECOMMENDATION.roundBudgetMax })}
                >
                  <Input
                    id="setting-budget-event"
                    type="number"
                    inputMode="numeric"
                    min={1}
                    max={RECOMMENDATION.roundBudgetMax}
                    value={budgetPerEvent}
                    disabled={!canWrite}
                    onChange={(event) => setBudgetPerEvent(event.target.value)}
                  />
                </Field>
                <Field
                  label={t('admin.settings.roundBudgetPerMember')}
                  htmlFor="setting-budget-member"
                >
                  <Input
                    id="setting-budget-member"
                    type="number"
                    inputMode="numeric"
                    min={1}
                    max={RECOMMENDATION.roundBudgetMax}
                    value={budgetPerMember}
                    disabled={!canWrite}
                    onChange={(event) => setBudgetPerMember(event.target.value)}
                  />
                </Field>
                <Field
                  label={t('admin.settings.roundBudgetPerAccountDaily')}
                  htmlFor="setting-budget-account"
                >
                  <Input
                    id="setting-budget-account"
                    type="number"
                    inputMode="numeric"
                    min={1}
                    max={RECOMMENDATION.roundBudgetMax}
                    value={budgetPerAccountDaily}
                    disabled={!canWrite}
                    onChange={(event) => setBudgetPerAccountDaily(event.target.value)}
                  />
                </Field>
              </FieldGroup>

              {invalidFields.length > 0 ? (
                <ErrorAlert
                  message={t('admin.settings.invalidValues', { fields: joinList(invalidFields, t) })}
                />
              ) : null}

              {errorText ? (
                <ErrorAlert message={errorText} />
              ) : null}

              {update.isSuccess ? (
                <Alert variant="success">
                  <AlertDescription>{t('admin.settings.saved')}</AlertDescription>
                </Alert>
              ) : null}

              {canWrite ? (
                <Button type="submit" className="self-start" disabled={update.isPending}>
                  {t('admin.settings.submit')}
                </Button>
              ) : (
                <p className="text-muted-foreground text-xs">{t('admin.settings.readOnly')}</p>
              )}
            </form>
          </CardContent>
        </Card>
      ) : null}
    </AdminPage>
  )
}
